import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, unref as _unref, createTextVNode as _createTextVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "recipe-form-page" }
const _hoisted_2 = { class: "form-header" }
const _hoisted_3 = { class: "recipe-form" }
const _hoisted_4 = { class: "form-section" }
const _hoisted_5 = { style: {"width":"100%"} }
const _hoisted_6 = { class: "upload-trigger" }
const _hoisted_7 = { class: "form-section" }
const _hoisted_8 = { class: "form-section" }
const _hoisted_9 = { class: "form-actions" }
const _hoisted_10 = { class: "icon-container" }
const _hoisted_11 = { class: "icon-container" }

import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  ElMessage,
  type FormInstance,
  type FormRules,
  type UploadFile,
  type UploadFiles,
  type UploadInstance,
} from 'element-plus';
import { Check, Back, UploadFilled } from '@element-plus/icons-vue';
import { useRecipeStore } from '@/stores/recipe';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import IngredientsList from '@/components/recipe/IngredientsList.vue';
import type {
  BackendRecipeCreateRequest,
  BackendRecipeUpdateRequest,
} from '@/types/backend';
import { convertIngredientsForBackend } from '@/utils/ingredientUtils';
import type { RecipeFormData } from '@/types/recipe';
import { imageUploadService } from '@/services/imageUploadService';


export default /*@__PURE__*/_defineComponent({
  __name: 'RecipeForm',
  setup(__props) {

const recipeStore = useRecipeStore();
const uiStore = useUIStore();
const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();

const formRef = ref<FormInstance>();
const uploadRef = ref<UploadInstance>();
const isEdit = computed(() => route.name === 'EditRecipe');
const recipeId = computed(() => route.params.id as string);

const fileList = ref<UploadFiles>([]);
const selectedFile = ref<File | null>(null);

const form = ref<RecipeFormData>({
  title: '',
  difficulty: '',
  ingredients: [{ ingredientName: '', amount: '' }],
  instructions: '',
  imageUrl: '',
});

const rules: FormRules = {
  title: [
    { required: true, message: 'Recipe title is required', trigger: 'blur' },
    {
      min: 3,
      max: 100,
      message: 'Title should be 3-100 characters',
      trigger: 'blur',
    },
  ],
  difficulty: [
    {
      required: true,
      message: 'Please select difficulty level',
      trigger: 'change',
    },
  ],
  imageUrl: [
    {
      required: true,
      message: 'Recipe image is required',
      trigger: 'change',
    },
  ],
  instructions: [
    { required: true, message: 'Instructions are required', trigger: 'blur' },
    {
      min: 10,
      message: 'Instructions should be at least 10 characters',
      trigger: 'blur',
    },
  ],
};

const updateIngredient = (
  index: number,
  field: 'ingredientName' | 'amount',
  value: string
) => {
  form.value.ingredients[index][field] = value;
};

const addIngredient = () => {
  form.value.ingredients.push({ ingredientName: '', amount: '' });
};

const removeIngredient = (index: number) => {
  if (form.value.ingredients.length > 1) {
    form.value.ingredients.splice(index, 1);
  }
};

const beforeUpload = (file: File): boolean => {
  const isValidType = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
  ].includes(file.type);
  const isValidSize = file.size / 1024 / 1024 < 5; // 5MB limit

  if (!isValidType) {
    ElMessage.error('Only JPG, PNG, and GIF images are allowed!');
    return false;
  }
  if (!isValidSize) {
    ElMessage.error('Image must be smaller than 5MB!');
    return false;
  }
  return true;
};

const handleFileChange = (file: UploadFile) => {
  if (file.raw) {
    selectedFile.value = file.raw;
    fileList.value = [file];
    // Update form value to trigger validation
    form.value.imageUrl = 'temp-value';
    // Manually trigger validation for imageUrl field
    formRef.value?.validateField('imageUrl');
  }
};

const handleFileRemove = async (file: UploadFile) => {
  try {
    // If this is an existing image (has a URL), delete it from the server
    if (file.url && file.url !== 'temp-value') {
      await imageUploadService.deleteImage(file.url);
      ElMessage.success('Image deleted successfully');
    }

    selectedFile.value = null;
    fileList.value = [];
    form.value.imageUrl = '';
    // Manually trigger validation for imageUrl field
    formRef.value?.validateField('imageUrl');
  } catch (error) {
    console.error('Error deleting image:', error);
    ElMessage.error('Failed to delete image');
  }
};

const validateIngredients = (): boolean => {
  const validIngredients = form.value.ingredients.filter(
    (ing) => (ing as any).name?.trim() || ing.ingredientName?.trim()
  );

  if (validIngredients.length === 0) {
    ElMessage.error('At least one ingredient is required');
    return false;
  }

  return true;
};

const validateImageUpload = (): boolean => {
  // Check if it's a new recipe and no image is selected
  if (!isEdit.value && !selectedFile.value && !form.value.imageUrl) {
    ElMessage.error('Please upload an image for your recipe');
    return false;
  }

  // Check if it's an edit and image was removed but no new one selected
  if (isEdit.value && !selectedFile.value && !form.value.imageUrl) {
    ElMessage.error('Please upload an image for your recipe');
    return false;
  }

  return true;
};

const handleSubmit = async () => {
  try {
    await formRef.value?.validate();

    if (!validateIngredients()) {
      return;
    }

    if (!validateImageUpload()) {
      return;
    }

    // Upload image first if a file is selected
    let imageUrl = form.value.imageUrl;
    if (selectedFile.value) {
      try {
        uiStore.setLoading(true);
        ElMessage.info('Uploading image...');
        imageUrl = await imageUploadService.uploadImage(selectedFile.value);
        ElMessage.success('Image uploaded successfully!');
      } catch (error) {
        console.error('Image upload failed:', error);
        ElMessage.error('Failed to upload image. Please try again.');
        return;
      }
    }

    // Use utility function to convert ingredients to backend format
    const ingredientNames = convertIngredientsForBackend(
      form.value.ingredients
    );

    if (isEdit.value) {
      const updateData: BackendRecipeUpdateRequest = {
        title: form.value.title,
        difficulty: form.value.difficulty as 'EASY' | 'MEDIUM' | 'HARD',
        instructions: form.value.instructions,
        imageUrl: imageUrl || '',
        creatorName: authStore.userDisplayName,
        ingredientNames: ingredientNames,
      };

      const result = await recipeStore.updateRecipe(recipeId.value, updateData);

      if (result) {
        ElMessage.success('Recipe updated successfully!');
        router.push('/');
      } else {
        ElMessage.error('Failed to update recipe');
      }
    } else {
      const createData: BackendRecipeCreateRequest = {
        title: form.value.title,
        difficulty: form.value.difficulty as 'EASY' | 'MEDIUM' | 'HARD',
        instructions: form.value.instructions,
        imageUrl: imageUrl || '',
        creatorName: authStore.userDisplayName,
        ingredientNames: ingredientNames,
      };

      const result = await recipeStore.createRecipe(createData);

      if (result) {
        ElMessage.success('Recipe created successfully!');
        router.push('/');
      } else {
        ElMessage.error('Failed to create recipe');
      }
    }
  } catch (error) {
    console.error('Form submission failed:', error);
    ElMessage.error('An error occurred while saving the recipe');
  }
};

const loadRecipeForEdit = async () => {
  if (isEdit.value && recipeId.value) {
    const recipe = await recipeStore.fetchRecipe(recipeId.value);
    if (recipe) {
      // Process ingredients to separate amount from ingredientName
      const processedIngredients =
        recipe.ingredients && recipe.ingredients.length > 0
          ? recipe.ingredients.map((ing) => {
              if (typeof ing === 'string') {
                return { ingredientName: ing, amount: '' };
              }

              // If ingredientName has amount prefixed (e.g., "2 eggs")
              const ingredientText = ing.ingredientName || '';
              const match = ingredientText.match(
                /^(\d+[\s/.0-9]*\s*\w*)\s+(.+)$/
              );

              if (match) {
                return {
                  ingredientName: match[2].trim(),
                  amount: match[1].trim(),
                };
              }

              return {
                ingredientName: ingredientText,
                amount: ing.amount || '',
              };
            })
          : [{ ingredientName: '', amount: '' }];

      form.value = {
        title: recipe.title,
        difficulty: recipe.difficulty,
        ingredients: processedIngredients,
        instructions: recipe.instructions || '',
        imageUrl: recipe.imageUrl || '',
        creatorName: recipe.creatorName || authStore.userDisplayName,
      };

      // If there's an existing image URL, show it in the upload component
      if (recipe.imageUrl) {
        fileList.value = [
          {
            name: 'existing-image',
            url: recipe.imageUrl,
            status: 'success',
            uid: Date.now(),
          },
        ];
      }
    }
  }
};

onMounted(() => {
  loadRecipeForEdit();
});

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_upload = _resolveComponent("el-upload")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_form = _resolveComponent("el-form")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h2", null, _toDisplayString(isEdit.value ? 'Edit Recipe' : 'Create New Recipe'), 1),
      _createElementVNode("p", null, _toDisplayString(isEdit.value
            ? 'Update your recipe details'
            : 'Share your culinary creation with others'), 1)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_component_el_form, {
        ref_key: "formRef",
        ref: formRef,
        model: form.value,
        rules: rules,
        "label-width": "140px",
        onSubmit: _withModifiers(handleSubmit, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_4, [
            _cache[6] || (_cache[6] = _createElementVNode("h3", null, "Basic Information", -1)),
            _createVNode(_component_el_form_item, {
              label: "Recipe Title",
              prop: "title"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.value.title,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.value.title) = $event)),
                  placeholder: "Enter recipe title",
                  maxlength: "100",
                  "show-word-limit": "",
                  size: "large"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "Difficulty Level",
              prop: "difficulty_level"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_select, {
                  modelValue: form.value.difficulty,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.difficulty) = $event)),
                  placeholder: "Select difficulty",
                  style: {"width":"100%"},
                  size: "large"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_option, {
                      label: "Easy",
                      value: "EASY"
                    }),
                    _createVNode(_component_el_option, {
                      label: "Medium",
                      value: "MEDIUM"
                    }),
                    _createVNode(_component_el_option, {
                      label: "Hard",
                      value: "HARD"
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "Recipe Image",
              prop: "imageUrl"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_component_el_upload, {
                    ref_key: "uploadRef",
                    ref: uploadRef,
                    action: '',
                    "auto-upload": false,
                    "on-change": handleFileChange,
                    "on-remove": handleFileRemove,
                    "before-upload": beforeUpload,
                    "file-list": fileList.value,
                    "list-type": "picture-card",
                    limit: 1,
                    accept: "image/*",
                    drag: ""
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_6, [
                        _createVNode(_component_el_icon, { class: "el-icon--upload" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(UploadFilled))
                          ]),
                          _: 1
                        }),
                        _cache[4] || (_cache[4] = _createElementVNode("div", { class: "el-upload__text" }, [
                          _createTextVNode(" Drop image here or "),
                          _createElementVNode("em", null, "click to upload")
                        ], -1)),
                        _cache[5] || (_cache[5] = _createElementVNode("div", { class: "el-upload__tip" }, " jpg/png files with size less than 5MB ", -1))
                      ])
                    ]),
                    _: 1
                  }, 8, ["file-list"])
                ])
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_7, [
            _cache[7] || (_cache[7] = _createElementVNode("h3", null, "Ingredients", -1)),
            _createVNode(_component_el_form_item, { prop: "ingredients" }, {
              default: _withCtx(() => [
                _createVNode(IngredientsList, {
                  ingredients: form.value.ingredients,
                  editable: true,
                  onUpdateIngredient: updateIngredient,
                  onAddIngredient: addIngredient,
                  onRemoveIngredient: removeIngredient
                }, null, 8, ["ingredients"])
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_8, [
            _cache[8] || (_cache[8] = _createElementVNode("h3", null, "Instructions", -1)),
            _createVNode(_component_el_form_item, { prop: "instructions" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.value.instructions,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.instructions) = $event)),
                  type: "textarea",
                  rows: 8,
                  placeholder: "Enter detailed cooking instructions...",
                  maxlength: "2000",
                  "show-word-limit": "",
                  size: "large"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createVNode(_component_el_button, {
              type: "primary",
              class: "custom-button",
              loading: _unref(uiStore).isLoading,
              size: "large",
              onClick: handleSubmit
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", _hoisted_10, [
                  _createVNode(_unref(Check), { class: "button-icon" })
                ]),
                _createTextVNode(" " + _toDisplayString(isEdit.value ? 'Update Recipe' : 'Create Recipe'), 1)
              ]),
              _: 1
            }, 8, ["loading"]),
            _createVNode(_component_el_button, {
              class: "custom-button-secondary",
              size: "large",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$router.push('/')))
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", _hoisted_11, [
                  _createVNode(_unref(Back), { class: "button-icon" })
                ]),
                _cache[9] || (_cache[9] = _createTextVNode(" Cancel "))
              ]),
              _: 1,
              __: [9]
            })
          ])
        ]),
        _: 1
      }, 8, ["model"])
    ])
  ]))
}
}

})